/**
 * The liquidity-dropout mini game (Doom 10-17) on Game Detail, beside the harvester's control: a small button that says
 * whether the game's mini game is running (and live or practice), opening to its panel (MiniGamePanel — the approved
 * side panel, the same one the Penelope ticket shows). Its own row, separate from Penelope's. It reads the game's
 * Penelope state through the page's shared PenelopeSidesProvider (the book switches already poll it), so the
 * collapsed button costs no extra relay reads.
 */
import { useState } from 'react';
import { Dices } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { penelopeEventTicker } from '@/hooks/usePenelope';
import { usePenelopeAccess } from '@/hooks/usePenelopeAccess';
import { usePenelopeProfile } from '@/hooks/usePenelopeProfile';
import { PENELOPE_EVENT_PREFIXES, penelopeSportOfEvent, usePenelopeSides } from '@/hooks/usePenelopeSides';
import { MiniGamePanel } from '@/views/LiveGame/MiniGamePanel';
import type { GameRow } from '@/views/ValueDashboard/types';

const OPEN_KEY = 'g1.miniGameControl.open';
const readOpen = (): boolean => {
  try { return localStorage.getItem(OPEN_KEY) === '1'; } catch { return false; }
};
const writeOpen = (open: boolean): void => {
  try { localStorage.setItem(OPEN_KEY, open ? '1' : '0'); } catch { /* per-viewer convenience only */ }
};

export function MiniGameControl({ game }: { game: GameRow }) {
  const access = usePenelopeAccess();
  const event = penelopeEventTicker(game);
  const supported = event !== null && PENELOPE_EVENT_PREFIXES.some((p) => event.startsWith(p));
  const [open, setOpen] = useState(readOpen);
  const sides = usePenelopeSides(game, access && supported);
  // R9: this box's ranges for the panel's fields (Max Long 50,000, Contract Size 10,000 on node1), read while it is open.
  const bounds = usePenelopeProfile(access && supported && open, penelopeSportOfEvent(event)).data?.bounds;
  if (!access || !supported) return null;
  const toggle = () => { setOpen(!open); writeOpen(!open); };
  const control = sides.control;
  const m = control.minigame;
  const row = m?.game ?? null;
  const armed = row?.config.enabled === true;
  const isLive = row?.config.mode === 'live';
  const state = !row ? (m?.lastStopped ? 'stopped' : null) : armed ? `running · ${isLive ? 'live' : 'practice'}` : `off · ${isLive ? 'live' : 'practice'}`;
  if (!open || !m) {
    return (
      <button type="button" onClick={toggle} data-testid="minigame-open"
        className={cn('mt-1 flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
          armed ? (isLive ? 'border-red-500 text-red-500' : 'border-sky-500 text-sky-500') : 'border-border/60 text-muted-foreground hover:text-foreground')}
        title="The mini game: small bids on both teams during a two-sided liquidity dropout, a few seconds at a time">
        <Dices className="h-3 w-3" /> mini game{state ? ` · ${state}` : ''}
      </button>
    );
  }
  return (
    <div className="mt-1 max-w-[492px] space-y-1" data-testid="minigame-control">
      <div className="flex justify-end">
        <button type="button" onClick={toggle} className="text-muted-foreground hover:text-foreground text-[10px] uppercase">close</button>
      </div>
      <MiniGamePanel game={game} m={m} busy={control.busy} error={control.error} bounds={bounds} />
    </div>
  );
}
